import {forwardRef} from 'react';
import {Button} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';

import foodSpend1Avif from '@/assets/images/home/food-spend-1.avif';
import foodSpend1Png from '@/assets/images/home/food-spend-1.png';
import foodSpend1Webp from '@/assets/images/home/food-spend-1.webp';
import foodSpend2Avif from '@/assets/images/home/food-spend-2.avif';
import foodSpend2Png from '@/assets/images/home/food-spend-2.png';
import foodSpend2Webp from '@/assets/images/home/food-spend-2.webp';
import foodSpendMobileAvif from '@/assets/images/home/food-spend-mobile.avif';
import foodSpendMobilePng from '@/assets/images/home/food-spend-mobile.png';
import foodSpendMobileWebp from '@/assets/images/home/food-spend-mobile.webp';
import List from '@/components/ui/List';
import useTracking from '@/hooks/useTracking';
import {FOOD_SPEND_MANAGEMENT_PATH} from '@/paths';

const FoodSpend = forwardRef<HTMLElement, unknown>(function FoodSpend(_props, ref) {
  const {t} = useTranslation('home');
  const {trackClick} = useTracking();
  const bulletList = [t('foodSpend.bullet1'), t('foodSpend.bullet2'), t('foodSpend.bullet3')];

  const trackCtaClick = () => {
    trackClick('food-spend-cta');
  };

  return (
    <section ref={ref} className="bg-green-50 tablet:py-32">
      <div className="no-wrap flex flex-col justify-center tablet:flex-row tablet:gap-8">
        <picture className="overflow-hidden tablet:hidden">
          <source srcSet={foodSpendMobileAvif.src} type="image/avif" />
          <source srcSet={foodSpendMobileWebp.src} type="image/webp" />
          <source srcSet={foodSpendMobilePng.src} type="image/png" />
          <img
            alt={t('foodSpend.imageAlt2')}
            className="h-full w-full object-cover"
            decoding="async"
            height={434}
            loading="lazy"
            src={foodSpendMobilePng.src}
            width={412}
          />
        </picture>

        <picture className="hidden overflow-hidden tablet:block">
          <source srcSet={foodSpend1Avif.src} type="image/avif" />
          <source srcSet={foodSpend1Webp.src} type="image/webp" />
          <source srcSet={foodSpend1Png.src} type="image/png" />
          <img
            alt={t('foodSpend.imageAlt1')}
            className="h-full w-full object-cover object-right"
            decoding="async"
            height={434}
            loading="lazy"
            src={foodSpend1Png.src}
            width={412}
          />
        </picture>

        <div className="relative flex w-full shrink-0 flex-col justify-center gap-6 rounded-2xl p-6 pb-12 tablet:h-[434px] tablet:w-[470px] tablet:bg-white tablet:p-12">
          <h2 className="text-[32px] font-bold leading-[38px]">{t('foodSpend.title')}</h2>
          <List items={bulletList} keyPrefix="food-spend-detail" className="text-lg" />
          <Button
            className="w-full tablet:w-fit"
            render={
              <a data-testid="food-spend-cta" href={FOOD_SPEND_MANAGEMENT_PATH}>
                {t('foodSpend.cta')}
              </a>
            }
            onClick={trackCtaClick}
          />
        </div>

        <picture className="hidden overflow-hidden tablet:block">
          <source srcSet={foodSpend2Avif.src} type="image/avif" />
          <source srcSet={foodSpend2Webp.src} type="image/webp" />
          <source srcSet={foodSpend2Png.src} type="image/png" />
          <img
            alt={t('foodSpend.imageAlt2')}
            className="h-full w-full object-cover object-left"
            decoding="async"
            height={434}
            loading="lazy"
            src={foodSpend2Png.src}
            width={650}
          />
        </picture>
      </div>
    </section>
  );
});

export default FoodSpend;
